import Carregando from '../components/Carregando';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  TextField,
  InputAdornment,
  LinearProgress,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import api from '../services/api';
import { formatDias } from '../utils/formatters';
import PageHeader from '../components/PageHeader';
import ContratosProjetos from '../components/contratos/ContratosProjetos';

const statusConfig = {
  planejamento: { label: 'Planejamento', color: 'default'  },
  em_andamento: { label: 'Em andamento', color: 'primary'  },
  pausado:      { label: 'Pausado',      color: 'warning'  },
  concluido:    { label: 'Concluído',    color: 'success'  },
  cancelado:    { label: 'Cancelado',    color: 'error'    },
};

const statusHex = {
  planejamento: '#94A3B8',
  em_andamento: '#2563EB',
  pausado:      '#D97706',
  concluido:    '#16A34A',
  cancelado:    '#DC2626',
};

function AnelMini({ pct, cor, semDado = false }) {
  return (
    <Box sx={{
      width: 56, height: 56, borderRadius: '50%', flexShrink: 0,
      background: `conic-gradient(${cor} ${pct * 3.6}deg, #eef0f3 0deg)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <Box sx={{
        width: 44, height: 44, borderRadius: '50%', backgroundColor: 'white',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13, color: '#1a1a2e' }}>
          {semDado ? '—' : `${pct}%`}
        </Typography>
      </Box>
    </Box>
  );
}

function BarraMini({ label, valor, total, pct, cor, alerta }) {
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.4 }}>
        <Typography sx={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#94a3b8', fontWeight: 700 }}>
          {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
          {alerta && <WarningAmberIcon sx={{ fontSize: 13, color: '#D97706' }} />}
          <Typography sx={{ fontSize: 10.5, fontFamily: 'monospace', color: '#64748b' }}>
            {valor} / {total}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ height: 5, borderRadius: 3, backgroundColor: '#eef0f3', overflow: 'hidden' }}>
        <Box sx={{ height: '100%', width: `${Math.min(pct, 100)}%`, backgroundColor: cor, borderRadius: 3, transition: 'width 0.3s ease' }} />
      </Box>
    </Box>
  );
}

export function CartaoProjeto({ p, navigate }) {
  const st = statusConfig[p.status] || statusConfig.planejamento;
  const num = (v) => Number(v) || 0;
  const percentual = (feito, total) => (total > 0 ? Math.min(Math.round((feito / total) * 100), 100) : 0);

  // Projeto de contrato novo: a carga e os workshops são do contrato (pool único), então o projeto não tem
  // "contratada" própria. O cartão mostra o andamento do MÊS: realizado (Agenda) / planejado do mês
  // (planejamento mensal feito pelo Diretor). Sem planejamento para o mês, não usa a Agenda no lugar:
  // mostra "—" e avisa. Projeto antigo (1 contrato = 1 projeto) continua como sempre.
  const ci = p.contrato_info;
  const doMes = Boolean(ci);
  const mes = p.carga_do_mes;                                   // null = ainda não carregou / falhou
  const planejado = doMes && Boolean(mes?.planejamento_preenchido);
  const semPlano = doMes && !planejado;

  const cargaFeita = doMes ? num(mes?.consultoria_realizada ?? p.carga_realizada) : num(p.carga_realizada);
  const cargaTotal = doMes ? num(mes?.consultoria_planejada) : num(p.carga_total_contratada);
  const wsFeitos = doMes ? num(mes?.workshop_realizado) : num(p.workshops_realizados);
  const wsTotal = doMes ? num(mes?.workshop_planejado) : num(p.workshops_vendidos);
  const pctCarga = semPlano ? 0 : percentual(cargaFeita, cargaTotal);
  const pctWorkshop = semPlano ? 0 : percentual(wsFeitos, wsTotal);
  const formatarWs = (v) => (doMes ? formatDias(v) : v);
  return (
          <Grid item xs={12} sm={6} md={4} key={p.id}>
            <Card
              sx={{
                height: '100%', cursor: 'pointer', borderRadius: 3, overflow: 'hidden',
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 12px 28px rgba(15,23,42,0.12)' },
              }}
              onClick={() => navigate(`/projetos/${p.id}`)}
            >
              <Box sx={{ backgroundColor: statusHex[p.status] || statusHex.planejamento, height: 5 }} />
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                    {p.nome}
                  </Typography>
                  <Chip label={st.label} color={st.color} size="small" sx={{ flexShrink: 0 }} />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, color: '#64748b', mb: p.contrato_novo_nome ? 0.5 : 2.5 }}>
                  <BusinessIcon sx={{ fontSize: 14 }} />
                  <Typography variant="body2">{p.cliente_nome}</Typography>
                </Box>
                {p.contrato_novo_nome && (
                  <Typography variant="caption" sx={{ display: 'block', color: '#64748b', mb: 2.5 }}>
                    Contrato: {p.contrato_novo_nome}
                  </Typography>
                )}

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                  <AnelMini pct={pctCarga} cor="#13375F" semDado={semPlano} />
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.3 }}>
                    <BarraMini
                      label={doMes ? 'Carga do mês' : 'Carga'} cor="#13375F" pct={pctCarga} alerta={!doMes && pctCarga >= 80}
                      valor={formatDias(cargaFeita)} total={semPlano ? '—' : formatDias(cargaTotal)}
                    />
                    <BarraMini
                      label={doMes ? 'Workshop do mês' : 'Workshop'} cor="#e94560" pct={pctWorkshop} alerta={!doMes && pctWorkshop >= 80}
                      valor={formatarWs(wsFeitos)} total={semPlano ? '—' : formatarWs(wsTotal)}
                    />
                  </Box>
                </Box>

                {p.membros_equipe_nomes?.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, pt: 1.5, borderTop: '1px solid #f0f0f0' }}>
                    {p.membros_equipe_nomes.map((nome, i) => (
                      <Box
                        key={i}
                        sx={{ backgroundColor: '#f1f3f6', color: '#1a1a2e', px: 1, py: 0.3, borderRadius: 1, fontSize: 11, fontWeight: 600 }}
                      >
                        {nome}
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
  );
}

export default function Projetos() {
  const navigate = useNavigate();

  const [projetos, setProjetos] = useState([]);
  const [contratos, setContratos] = useState(null);   // null = sem o módulo "Contratos" (ou falhou): tela de sempre
  const [busca, setBusca] = useState('');
  const [loading, setLoading] = useState(true);
  const [sucesso, setSucesso] = useState('');

  const carregar = () => {
    Promise.allSettled([
      api.get('/projetos/'),
      api.get('/contratos/'),
    ]).then(([proj, contr]) => {
      if (proj.status === 'fulfilled') setProjetos(proj.value.data);
      setContratos(contr.status === 'fulfilled' && contr.value ? contr.value.data : null);
      setLoading(false);
    });
  };

  useEffect(() => { carregar(); }, []);

  const filtrados = projetos.filter((p) =>
    p.nome?.toLowerCase().includes(busca.toLowerCase()) ||
    p.cliente_nome?.toLowerCase().includes(busca.toLowerCase())
  );

  // Com o módulo "Contratos": cada contrato novo é um grupo com os projetos dele; os projetos antigos
  // (1 contrato = 1 projeto) ficam na lista de sempre, logo abaixo.
  const comGrupos = contratos !== null;
  const soltos = comGrupos ? filtrados.filter((p) => !p.contrato_novo_id) : filtrados;

  return (
    <Box>
      <PageHeader titulo="Projetos" />

      <TextField
        fullWidth
        placeholder="Buscar por nome ou cliente..."
        value={busca}
        onChange={(e) => setBusca(e.target.value)}
        sx={{ mb: 3 }}
                slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          },
        }}
      />

      {sucesso && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSucesso('')}>
          {sucesso}
        </Alert>
      )}

      {loading ? (
        <Carregando />
      ) : (
        <>
          {comGrupos && (
            <ContratosProjetos
              contratos={contratos}
              projetos={filtrados}
              busca={busca}
              renderProjeto={(p) => <CartaoProjeto key={p.id} p={p} navigate={navigate} />}
              aoAdicionarProjeto={carregar}
            />
          )}

          {comGrupos && contratos.length > 0 && soltos.length > 0 && (
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5, mt: 1 }}>Projetos anteriores (contrato de 1 projeto)</Typography>
          )}

          {soltos.length === 0 ? (
            !comGrupos || contratos.length === 0 ? (
              <Card>
                <CardContent>
                  <Typography color="text.secondary" textAlign="center">
                    Nenhum projeto encontrado.
                  </Typography>
                </CardContent>
              </Card>
            ) : null
          ) : (
            <Grid container spacing={2}>
              {soltos.map((p) => <CartaoProjeto key={p.id} p={p} navigate={navigate} />)}
            </Grid>
          )}
        </>
      )}
    </Box>
  );
}